<template>
  <transition :name="animation" appear>
    <div v-if="isConfirmationExpanded" class="confirmation">
      <transition name="slide-fade" mode="out-in">
        <div v-if="isBusy" class="col-l-12">
          <VSpinner style="padding-top: 30px; padding-bottom: 40px;" />
        </div>
        <VNotification
          v-else-if="isSuccess"
          :key="type"
          v-bind="modelValue.message"
          inner-class="offset-bottom-1"
          icon="icon-confirm icon-large"
          middle
          closable
          @close="onClose"
        >
          <span class="notification-heading">
            <slot name="success" v-bind="modelValue"><span v-html="content"/></slot>
          </span>
        </VNotification>
        <VNotification
          v-else-if="isError"
          :key="type"
          :heading="heading"
          v-bind="modelValue.message"
          inner-class="offset-bottom-1"
          center
          closable
          @close="onClose"
        >
          <slot name="error" v-bind="modelValue"><span v-html="content"/></slot>
        </VNotification>
        <VNotification
          v-else
          :key="'prompt_' + swapHighlighting"
          :heading="heading"
          type="PROMPT"
          inner-class="offset-bottom-1"
          :closable="closable"
          center
          lock
          @close="onClose"
        >
          <div class="prompt-wrapper">
            <p><slot name="prompt" v-bind="modelValue"/></p>
            <slot name="beforeContent" v-bind="modelValue"></slot>
            <slot name="content" v-bind="modelValue">
              <button
                v-focus="!swapHighlighting"
                type="button"
                class="btn btn-small"
                :class="{ 'btn-brand': !swapHighlighting, 'btn-default': swapHighlighting }"
                :title="translateTitle('yes')"
                :disabled="isBusy"
                v-bind="confirmAttrs"
                @click="$emit('confirm', modelValue)"
              >
                {{ confirmButtonLabelWithDefault }}
              </button>
              <button
                v-focus="swapHighlighting"
                type="button"
                class="btn btn-small"
                :class="{ 'btn-brand': swapHighlighting, 'btn-default': !swapHighlighting }"
                :title="translateTitle('no')"
                :disabled="isBusy"
                @click="onClose"
              >
                {{ abortButtonLabelWithDefault }}
              </button>
            </slot>
          </div>
        </VNotification>
      </transition>
    </div>
  </transition>
</template>

<script>
import VSpinner from '@/components/Spinner';
import VNotification from '@/components/Notification';
import focus from '@/directives/focus';

export default {
  name: 'Confirmation',

  directives: {
    focus,
  },

  components: {
    VSpinner,
    VNotification,
  },

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    id: {
      type: String,
      required: false,
      default: undefined,
    },

    heading: {
      type: String,
      required: false,
      default: null,
    },

    modelValue: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },

    isBusy: {
      type: Boolean,
      required: false,
    },

    animation: {
      type: String,
      required: false,
      default: 'slide-fade2',
    },

    title: {
      type: String,
      required: false,
      default: 'common.tooltipConfirmation',
    },

    confirmButtonLabel: {
      type: String,
      required: false,
      default: null,
    },

    abortButtonLabel: {
      type: String,
      required: false,
      default: null,
    },

    swapHighlighting: {
      type: Boolean,
      required: false,
    },

    closable: {
      type: Boolean,
      required: false,
    },

    noFocusRestore: {
      type: Boolean,
      required: false,
    },

    confirmAttrs: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      previousElement: document.activeElement,
    };
  },

  computed: {
    isConfirmationExpanded() {
      return this.id === undefined || this.modelValue?.id === this.id;
    },

    content() {
      if (this.modelValue?.message?.supplier) return this.modelValue.message.supplier();
      return this.$to(this.modelValue?.message?.content);
    },

    type() {
      return this.modelValue?.message?.type;
    },

    isSuccess() {
      return this.type === 'SUCCESS';
    },

    isError() {
      return this.type === 'ERROR';
    },

    confirmButtonLabelWithDefault() {
      return this.confirmButtonLabel ?? this.$t('common.yes_continue');
    },

    abortButtonLabelWithDefault() {
      return this.abortButtonLabel ?? this.$t('common.no_cancel');
    },
  },

  watch: {
    modelValue: 'onValue',
    isConfirmationExpanded: {
      handler(newVal) {
        if (newVal) this.$emit('expand');
      },
      immediate: true,
    },
  },

  methods: {
    onClose() {
      // is called when clicking the "no" button, hitting esc or clicking the x-button
      this.$emit('abort');
      this.$emit('update:modelValue', null);
      if (!this.noFocusRestore) {
        this.$nextTick(() => this.previousElement.focus());
      }
    },

    translateTitle(v) {
      const key = this.title + v.charAt(0).toUpperCase() + v.substring(1);
      if (this.title !== '' && this.$te(key)) {
        return this.$t(key);
      }
      return null;
    },

    onValue(to, from) {
      if (!this.modelValue) return;
      if (this.modelValue.message) return;
      this.previousElement = document.activeElement;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';
.prompt-wrapper .btn {
  margin-right: 6px;
  margin-left: 6px;
}

@media screen and (max-width: #{$grid-medium-min}px) {
  .prompt-wrapper .btn {
    width: 100%;
    margin-bottom: 12px;
  }
}
</style>
